import type {
  PathSegment,
  Position,
  VoronoiMazeData,
  VoronoiRegion,
} from '../types/maze-types';
import { computeEmojiPosition } from './voronoiEmojiPosition';
import { extractVoronoiWalls, isPathCrossingVoronoiWall } from './voronoiWalls';

/** Ray-casting point-in-polygon. */
function pointInPolygon(p: Position, poly: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    if (yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Compute average cell diameter from all regions (used for proximity thresholds).
 */
function computeAvgCellDiameter(regions: VoronoiRegion[]): number {
  if (regions.length === 0) return 50;
  let totalDiam = 0;
  for (const region of regions) {
    const xs = region.vertices.map((v) => v.x);
    const ys = region.vertices.map((v) => v.y);
    if (xs.length === 0) continue;
    const w = Math.max(...xs) - Math.min(...xs);
    const h = Math.max(...ys) - Math.min(...ys);
    totalDiam += (w + h) / 2;
  }
  return totalDiam / regions.length;
}

/**
 * Squared distance between two points.
 */
function distSq(a: Position, b: Position): number {
  return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
}

/**
 * Check whether a drawn path completes a Voronoi maze.
 *
 * Key insight: `isPathCrossingVoronoiWall` already prevents drawing through
 * walls via geometric line-segment intersection during drawing. So if a path
 * exists, it CANNOT cross walls. Completion just needs to verify the path
 * connects both emojis.
 *
 * Algorithm:
 * 1. Compute entry/exit emoji positions (same as renderer).
 * 2. Build point-to-point connectivity graph from all path segments:
 *    - Consecutive points within same segment -> always connected.
 *    - Endpoints of DIFFERENT segments within CONNECTION_THRESHOLD -> connected
 *      (supports multi-segment paths where user lifts finger).
 * 3. Find points near entry emoji and points near exit emoji (within proximity).
 * 4. BFS from any "near entry" point to any "near exit" point through the graph.
 * 5. Return true if connected.
 */
export function checkVoronoiCompletion(
  pathSegments: PathSegment[],
  mazeData: VoronoiMazeData,
): boolean {
  if (!pathSegments || pathSegments.length === 0) return false;

  const { regions, entryRegionId, exitRegionId } = mazeData;
  if (regions.length === 0) return false;

  const regionById = new Map<number, VoronoiRegion>();
  for (const region of regions) regionById.set(region.id, region);
  if (!regionById.has(entryRegionId) || !regionById.has(exitRegionId)) return false;

  // Same region = trivially completed if any point exists
  if (entryRegionId === exitRegionId) {
    return pathSegments.some((seg) => seg.points.length > 0);
  }

  // 1. Compute emoji positions (matching renderer logic)
  const entryRegion = regionById.get(entryRegionId)!;
  const exitRegion = regionById.get(exitRegionId)!;
  const entryPos = computeEmojiPosition(entryRegion, regions, mazeData.contour);
  const exitPos = computeEmojiPosition(exitRegion, regions, mazeData.contour);

  const avgCellDiam = computeAvgCellDiameter(regions);
  const EMOJI_PROXIMITY = avgCellDiam * 0.8;
  const EMOJI_PROXIMITY_SQ = EMOJI_PROXIMITY * EMOJI_PROXIMITY;
  const CONNECTION_THRESHOLD = Math.max(12, avgCellDiam * 0.5); // ≥ a finger-width junction on dense mazes
  const CONNECTION_THRESHOLD_SQ = CONNECTION_THRESHOLD * CONNECTION_THRESHOLD;

  // 2. Collect all points, flattened with a global index.
  //    Track segment boundaries for building connectivity.
  interface PointEntry {
    pos: Position;
    segmentIndex: number;
    pointIndex: number;
  }
  const allPoints: PointEntry[] = [];
  for (let si = 0; si < pathSegments.length; si++) {
    const pts = pathSegments[si].points;
    for (let pi = 0; pi < pts.length; pi++) {
      allPoints.push({ pos: pts[pi], segmentIndex: si, pointIndex: pi });
    }
  }

  if (allPoints.length === 0) return false;

  // 3. Find path points that reached the entry / exit ROOM: inside the tinted
  //    cell polygon (the room IS the target the player sees — the glyph may sit
  //    anywhere in it). Marker proximity is only the fallback for a degenerate
  //    polygon; used as an OR it let a path 70 px away in the NEXT room count.
  const nearEntry: number[] = [];
  const nearExit: number[] = [];
  const entryPoly = entryRegion.vertices.length >= 3 ? entryRegion.vertices : null;
  const exitPoly = exitRegion.vertices.length >= 3 ? exitRegion.vertices : null;
  const reached = (p: Position, poly: Position[] | null, marker: Position) =>
    poly ? pointInPolygon(p, poly) : distSq(p, marker) <= EMOJI_PROXIMITY_SQ;
  for (let i = 0; i < allPoints.length; i++) {
    const p = allPoints[i].pos;
    if (reached(p, entryPoly, entryPos)) nearEntry.push(i);
    if (reached(p, exitPoly, exitPos)) nearExit.push(i);
  }

  if (nearEntry.length === 0 || nearExit.length === 0) return false;

  // 4. Build adjacency list for the connectivity graph.
  //    - Consecutive points in the same segment are connected.
  //    - Endpoints of different segments within CONNECTION_THRESHOLD are connected.
  const adj = new Map<number, number[]>();
  const addEdge = (a: number, b: number) => {
    if (!adj.has(a)) adj.set(a, []);
    if (!adj.has(b)) adj.set(b, []);
    adj.get(a)!.push(b);
    adj.get(b)!.push(a);
  };

  // Consecutive points within each segment
  for (let i = 1; i < allPoints.length; i++) {
    if (allPoints[i].segmentIndex === allPoints[i - 1].segmentIndex) {
      addEdge(i - 1, i);
    }
  }

  // Cross-segment connections: a segment's ENDPOINT joins ANY point of another
  // segment within CONNECTION_THRESHOLD — not only that segment's endpoints.
  // Players resume a route by starting the next stroke on the middle of the
  // previous line; endpoint-to-endpoint matching left such junctions unlinked
  // ("drew it in three strokes, it never counted"). Grid-hashed: O(points).
  // …and only if the join itself does not cross a wall: two strokes may end a
  // finger apart in NEIGHBOURING rooms with a wall between them — that is not
  // a junction (drawing across that wall is refused, so joining across it must be too).
  const walls = extractVoronoiWalls(mazeData);
  const cell = Math.max(CONNECTION_THRESHOLD, 1);
  const grid = new Map<string, number[]>();
  const keyOf = (x: number, y: number) => `${Math.floor(x / cell)},${Math.floor(y / cell)}`;
  for (let i = 0; i < allPoints.length; i++) {
    const k = keyOf(allPoints[i].pos.x, allPoints[i].pos.y);
    const bucket = grid.get(k);
    if (bucket) bucket.push(i);
    else grid.set(k, [i]);
  }
  let offset = 0;
  for (let si = 0; si < pathSegments.length; si++) {
    const len = pathSegments[si].points.length;
    if (len === 0) continue;
    const ends = len > 1 ? [offset, offset + len - 1] : [offset];
    for (const gi of ends) {
      const p = allPoints[gi].pos;
      const gx = Math.floor(p.x / cell);
      const gy = Math.floor(p.y / cell);
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const bucket = grid.get(`${gx + dx},${gy + dy}`);
          if (!bucket) continue;
          for (const gj of bucket) {
            if (allPoints[gj].segmentIndex === si) continue;
            if (distSq(p, allPoints[gj].pos) > CONNECTION_THRESHOLD_SQ) continue;
            if (isPathCrossingVoronoiWall(p, allPoints[gj].pos, walls)) continue;
            addEdge(gi, gj);
          }
        }
      }
    }
    offset += len;
  }

  // 5. BFS from any "near entry" point to any "near exit" point
  const exitSet = new Set(nearExit);
  const visited = new Set<number>();
  const queue: number[] = [];

  for (const idx of nearEntry) {
    visited.add(idx);
    queue.push(idx);
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (exitSet.has(current)) return true;

    const neighbors = adj.get(current);
    if (!neighbors) continue;
    for (const next of neighbors) {
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    }
  }

  return false;
}
